import { BOOK_DISPLAY_ORDER } from './bookLogoRegistry.js'
import { buildConsensusFairValue, expectedValuePercent } from './liveOddsFairValueV1.js'

const SIGNED_LINE_MARKETS = new Set(['Spread', 'Run Line', 'Puck Line', 'Handicap'])

function token(value) {
  if (value === null || value === undefined || value === '') return '∅'
  return String(value)
}

function period(value) {
  return value == null || value === '' ? 'Game' : String(value)
}

function familyLine(row) {
  if (row?.line === null || row?.line === undefined || row?.line === '') return null
  const line = Number(row?.line)
  if (!Number.isFinite(line)) return null
  return SIGNED_LINE_MARKETS.has(row.bet_type) ? Math.abs(line) : line
}

function eventKey(row) {
  return [row.league, row.event, row.et_date].map(token).join('|')
}

function marketKey(row) {
  return [period(row.period), row.bet_type, familyLine(row)].map(token).join('|')
}

function mode(values) {
  const counts = new Map()
  for (const value of values.filter((item) => item != null && item !== '')) {
    counts.set(value, (counts.get(value) || 0) + 1)
  }
  return [...counts].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))[0]?.[0] ?? null
}

function compareRows(left, right) {
  if (left?.is_primary === true && right?.is_primary !== true) return -1
  if (right?.is_primary === true && left?.is_primary !== true) return 1
  return Number(right?.american ?? -Infinity) - Number(left?.american ?? -Infinity)
}

function orderedBooks(values) {
  const rank = new Map(BOOK_DISPLAY_ORDER.map((book, index) => [book, index]))
  return [...new Set(values.filter(Boolean))].sort((left, right) => {
    const a = rank.get(left) ?? Number.MAX_SAFE_INTEGER
    const b = rank.get(right) ?? Number.MAX_SAFE_INTEGER
    return a - b || left.localeCompare(right)
  })
}

function lineSignConflicts(rows) {
  if (!SIGNED_LINE_MARKETS.has(rows[0]?.bet_type)) return []
  const signs = new Map()
  for (const row of rows) {
    const line = Number(row?.line)
    if (!row?.selection || !Number.isFinite(line)) continue
    if (!signs.has(row.selection)) signs.set(row.selection, new Set())
    signs.get(row.selection).add(Math.sign(line))
  }
  return [...signs].filter(([, values]) => values.size > 1).map(([selection]) => selection).sort()
}

function unavailableFair(selections, books, settings, reason) {
  return {
    method: settings.method ?? 'multiplicative',
    weighting: settings.weighting ?? 'equal',
    selections,
    bySelection: Object.fromEntries(selections.map((selection) => [selection, {
      probability: null, american: null, contributingBooks: [], bookCount: 0,
    }])),
    excludedBooks: books,
    disabledReason: reason,
  }
}

function buildMarket(rows, settings) {
  const first = rows[0]
  const selections = [...new Set(rows.map((row) => row.selection).filter(Boolean))].sort()
  const books = orderedBooks(rows.map((row) => row.book))
  const candidates = {}
  for (const row of rows) {
    candidates[row.selection] ||= {}
    const current = candidates[row.selection][row.book]
    if (!current || compareRows(row, current) < 0) candidates[row.selection][row.book] = row
  }
  const cells = Object.fromEntries(selections.map((selection) => [selection,
    Object.fromEntries(Object.entries(candidates[selection] || {}).map(([book, row]) => [
      book, { row, best: false, evPercent: null },
    ])),
  ]))
  for (const selection of selections) {
    const priced = Object.values(cells[selection]).filter((cell) => Number.isFinite(Number(cell.row.american)))
    const best = priced.reduce((value, cell) => Math.max(value, Number(cell.row.american)), -Infinity)
    for (const cell of priced) cell.best = Number(cell.row.american) === best
  }
  const conflictSelections = lineSignConflicts(rows)
  const integrityStatus = conflictSelections.length > 0 ? 'line_sign_conflict' : 'ok'
  const fair = integrityStatus === 'ok'
    ? buildConsensusFairValue(rows, settings)
    : unavailableFair(selections, books, settings, integrityStatus)
  for (const selection of selections) {
    const probability = fair.bySelection[selection]?.probability ?? null
    for (const cell of Object.values(cells[selection])) {
      cell.evPercent = expectedValuePercent(cell.row.american, probability)
    }
  }
  return {
    key: marketKey(first),
    period: period(first.period),
    betType: first.bet_type || 'Unknown market',
    line: familyLine(first),
    selections,
    books,
    cells,
    fair,
    integrityStatus,
    conflictSelections,
    rowCount: rows.length,
  }
}

export function buildLiveOddsMatrix(rows, settings = {}) {
  const eventBuckets = new Map()
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!row?.event || !row?.book || !row?.selection) continue
    const key = eventKey(row)
    if (!eventBuckets.has(key)) eventBuckets.set(key, [])
    eventBuckets.get(key).push(row)
  }

  const events = []
  for (const [key, eventRows] of eventBuckets) {
    const marketBuckets = new Map()
    for (const row of eventRows) {
      const key = marketKey(row)
      if (!marketBuckets.has(key)) marketBuckets.set(key, [])
      marketBuckets.get(key).push(row)
    }
    const first = eventRows[0]
    const markets = [...marketBuckets.values()].map((marketRows) => buildMarket(marketRows, settings))
      .sort((left, right) => left.period.localeCompare(right.period)
        || left.betType.localeCompare(right.betType)
        || Number(left.line ?? 0) - Number(right.line ?? 0))
    events.push({
      key,
      league: first.league || 'Unknown',
      event: first.event,
      etDate: first.et_date || null,
      etTime: mode(eventRows.map((row) => row.et_time)),
      books: orderedBooks(eventRows.map((row) => row.book)),
      markets,
      rowCount: eventRows.length,
    })
  }
  events.sort((left, right) => token(left.etDate).localeCompare(token(right.etDate))
    || left.league.localeCompare(right.league)
    || left.event.localeCompare(right.event))
  return {
    events,
    books: orderedBooks(events.flatMap((event) => event.books)),
    counts: {
      events: events.length,
      markets: events.reduce((sum, event) => sum + event.markets.length, 0),
      rows: events.reduce((sum, event) => sum + event.rowCount, 0),
    },
  }
}
